import React, { Suspense } from 'react';
import { useBreakpoint } from '../../../hooks/useMediaQuery';
import { useMobileOptimization } from '../../../hooks/useMobileOptimization';

// Lazy load layout components for better performance
const DesktopConversationLayout = React.lazy(() => 
  import('./DesktopConversationLayout').then(module => ({ default: module.DesktopConversationLayout }))
);

const TabletConversationLayout = React.lazy(() => 
  import('./TabletConversationLayout').then(module => ({ default: module.TabletConversationLayout }))
);

const MobileConversationLayout = React.lazy(() => 
  import('./MobileConversationLayout').then(module => ({ default: module.MobileConversationLayout }))
);

// Loading fallback component
const LayoutSkeleton: React.FC = () => (
  <div className="h-screen bg-neutral-50 animate-pulse">
    <div className="flex h-full">
      <div className="w-80 bg-white border-r border-neutral-200">
        <div className="p-4 space-y-4">
          <div className="h-8 bg-neutral-200 rounded-lg"></div>
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 bg-neutral-100 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 bg-white">
        <div className="h-16 border-b border-neutral-200 bg-neutral-50"></div>
        <div className="p-4 space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-neutral-100 rounded-lg"></div>
          ))}
        </div>
      </div>
      <div className="w-80 bg-white border-l border-neutral-200">
        <div className="p-4 space-y-4">
          <div className="h-8 bg-neutral-200 rounded-lg"></div>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-neutral-100 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export interface ResponsiveConversationLayoutProps {
  conversationId?: string;
  onConversationSelect?: (conversationId: string) => void;
  onConversationCreate?: () => void;
  selectedConversationId?: string;
  className?: string;
}

/**
 * Main responsive conversation layout that adapts to different screen sizes
 * Desktop-first approach: maintains excellent desktop experience while adding mobile support
 */
export const ResponsiveConversationLayout: React.FC<ResponsiveConversationLayoutProps> = ({
  conversationId,
  onConversationSelect,
  onConversationCreate,
  selectedConversationId,
  className,
}) => {
  const { isMobile, isTablet, isDesktop, current } = useBreakpoint();
  const { settings, isOptimized } = useMobileOptimization();

  // Props to pass down to all layouts
  const commonProps = {
    conversationId,
    onConversationSelect,
    onConversationCreate,
    selectedConversationId,
    optimizationSettings: settings,
    isOptimized,
  };

  // Desktop-first: Default to desktop layout, then adapt for smaller screens
  const renderLayout = () => {
    // Mobile layout (< 768px) - Single panel with overlays
    if (isMobile) {
      return <MobileConversationLayout {...commonProps} />;
    }
    
    // Tablet layout (768px - 1024px) - Two panel with collapsible intelligence
    if (isTablet) {
      return <TabletConversationLayout {...commonProps} />;
    }
    
    // Desktop layout (>= 1024px) - Full three panel experience
    return <DesktopConversationLayout {...commonProps} />;
  };

  return (
    <div 
      className={`
        conversation-layout conversation-layout--${current}
        ${className || ''}
        ${isOptimized ? 'conversation-layout--optimized' : ''}
      `}
      data-breakpoint={current}
      data-optimized={isOptimized}
    >
      <Suspense fallback={<LayoutSkeleton />}>
        {renderLayout()}
      </Suspense>
    </div>
  );
};

/**
 * Context provider for responsive layout state
 * Provides layout information to child components
 */
export const ResponsiveLayoutContext = React.createContext<{
  breakpoint: string;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isOptimized: boolean;
  settings: any;
}>({
  breakpoint: 'desktop',
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  isOptimized: false,
  settings: {},
});

export const ResponsiveLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { current, isMobile, isTablet, isDesktop } = useBreakpoint();
  const { settings, isOptimized } = useMobileOptimization();

  const value = {
    breakpoint: current,
    isMobile,
    isTablet,
    isDesktop,
    isOptimized,
    settings,
  };

  return (
    <ResponsiveLayoutContext.Provider value={value}>
      {children}
    </ResponsiveLayoutContext.Provider>
  );
};

/**
 * Hook to access responsive layout context
 */
export const useResponsiveLayout = () => {
  const context = React.useContext(ResponsiveLayoutContext);
  if (!context) {
    throw new Error('useResponsiveLayout must be used within ResponsiveLayoutProvider');
  }
  return context;
}; 